/**
 * Apunte Digital's premium ErrorBoundary.
 *
 * Wraps UI regions (TipTap editor, tree, etc.) and the app root: if a descendant
 * throws during render, instead of a blank screen an editorial, theme-aware
 * fallback is shown (SVG illustration + title + description + "Retry"/"Reload").
 * The error is logged to the console.
 *
 * `variant`:
 *  - "page": full-screen fallback (root / main region).
 *  - "region": compact card for an embedded section (backlinks, attachments…).
 */
import { msg } from "@lingui/core/macro";
import { type JSX, ErrorBoundary as SolidErrorBoundary } from "solid-js";
import { AlertTriangle, RefreshCw, RotateCcw } from "@fleet/icons";
import { cn } from "@apunte-digital/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@fleet/ui";
import { ErrorIllustration } from "@/components/ui/illustrations";

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /** Full screen ("page") or embedded card ("region"). */
  variant?: "page" | "region";
  /** Label for logs (e.g. "editor", "page-tree"). */
  label?: string;
  /** Custom title/description; otherwise generic text is used. */
  title?: string;
  description?: string;
}

function PageFallback(props: {
  error: unknown;
  reset: () => void;
  title?: string;
  description?: string;
}) {
  const { t } = useI18n();
  return (
    <div class="flex min-h-full flex-1 items-center justify-center p-4">
      <GlassSurface class="flex w-full max-w-md flex-col items-center space-y-6 p-8 text-center motion-safe:animate-fade-in">
        <div class="text-accent motion-safe:animate-scale-in">
          <ErrorIllustration class="w-44" />
        </div>
        <div class="space-y-2">
          <h2 class="font-serif text-2xl font-semibold tracking-tight text-foreground">
            {props.title ?? t(msg`Something went wrong`)}
          </h2>
          <p class="mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground">
            {props.description ??
              t(msg`We hit a snag while rendering this view. Your notes are safe — try again.`)}
          </p>
        </div>
        <div class="flex w-full gap-3">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RotateCcw class="h-4 w-4" />
            {t(msg`Reload`)}
          </Button>
          <Button class="flex-1 gap-2" onClick={() => props.reset()}>
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

function RegionFallback(props: {
  error: unknown;
  reset: () => void;
  title?: string;
  description?: string;
}) {
  const { t } = useI18n();
  return (
    <div
      role="alert"
      class="flex flex-col items-center gap-3 rounded-xl border border-danger/20 bg-danger/5 px-5 py-6 text-center motion-safe:animate-fade-in"
    >
      <div class="flex h-11 w-11 items-center justify-center rounded-full bg-danger/10 text-danger">
        <AlertTriangle class="h-5 w-5" />
      </div>
      <div class="space-y-1">
        <p class="text-sm font-semibold text-foreground">
          {props.title ?? t(msg`This section could not be loaded`)}
        </p>
        <p class="mx-auto max-w-xs text-xs leading-relaxed text-muted-foreground">
          {props.description ?? t(msg`An unexpected error occurred while rendering this content.`)}
        </p>
      </div>
      <Button size="sm" variant="outline" class="gap-1.5" onClick={() => props.reset()}>
        <RefreshCw class="h-3.5 w-3.5" />
        {t(msg`Retry`)}
      </Button>
    </div>
  );
}

export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  return (
    <SolidErrorBoundary
      fallback={(error: unknown, reset: () => void) => {
        // Log with the region label for diagnostics.
        console.error(`[ErrorBoundary${props.label ? `:${props.label}` : ""}]`, error);
        const isRegion = props.variant === "region";
        return (
          <div class={cn(!isRegion && "flex min-h-0 flex-1 flex-col")}>
            {isRegion ? (
              <RegionFallback
                error={error}
                reset={reset}
                title={props.title}
                description={props.description}
              />
            ) : (
              <PageFallback
                error={error}
                reset={reset}
                title={props.title}
                description={props.description}
              />
            )}
          </div>
        );
      }}
    >
      {props.children}
    </SolidErrorBoundary>
  );
}
